import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Video, Lock, Play, Folder, X, Dumbbell, Lightbulb, ChefHat, Heart, Target, User, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useGender } from '@/context/GenderContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;// Función para convertir URLs a embebibles
const getEmbedUrl = (url, source) => {
  if (!url) return null;
  
  // YouTube
  if (source === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) {
    let videoId = '';
    if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1]?.split('?')[0];
    }
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    }
  }
  
  // Google Drive - formato preview
  if (source === 'drive' || url.includes('drive.google.com')) {
    let fileId = '';
    
    if (url.includes('/file/d/')) {
      fileId = url.split('/file/d/')[1]?.split('/')[0]?.split('?')[0];
    } else if (url.includes('id=')) {
      fileId = url.split('id=')[1]?.split('&')[0];
    }
    
    if (fileId) {
      return `https://drive.google.com/file/d/${fileId}/preview`;
    }
  }
  
  return url;
};

// Verificar si necesita iframe
const needsIframe = (url, source) => {
  const isBunnyPlayer = !!url && /(player|iframe)\.mediadelivery\.net\/(play|embed)\//i.test(url);
  return source === 'youtube' || source === 'drive' ||
         url?.includes('youtube.com') || url?.includes('youtu.be') ||
         url?.includes('drive.google.com') || isBunnyPlayer;
};

// Definición de carpetas/categorías - Diseño profesional sin emojis
const CATEGORIES = {
  'videos_hombres': {
    name: 'Entrenamiento para Hombres',
    description: 'Rutinas diseñadas para Guerreros',
    Icon: Dumbbell,
    gender: 'male',
    color: 'blue'
  },
  'videos_mujeres': {
    name: 'Entrenamiento para Mujeres',
    description: 'Rutinas diseñadas para Guerreras',
    Icon: Dumbbell,
    gender: 'female',
    color: 'pink'
  },
  'metodos_entrenamiento': {
    name: 'Métodos de Entrenamiento Explicados',
    description: 'Técnicas y métodos detallados',
    Icon: Lightbulb,
    gender: 'all',
    color: 'yellow'
  },
  'tips_nutricion': {
    name: 'Tips de Nutrición',
    description: 'Consejos para tu alimentación',
    Icon: Heart,
    gender: 'all',
    color: 'red'
  },
  'recetas_saludables': {
    name: 'Recetas Saludables',
    description: 'Preparaciones nutritivas y deliciosas',
    Icon: ChefHat,
    gender: 'all',
    color: 'green'
  },
  'motivacion': {
    name: 'Motivación',
    description: 'Inspiración para tu día a día',
    Icon: Target,
    gender: 'all',
    color: 'orange'
  }
};

const VideosOrganized = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMale, isFemale, hasGenderSet, getText, theme } = useGender();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [groupedVideos, setGroupedVideos] = useState({});
  const [userPlan, setUserPlan] = useState('none');
  const [hasVideoAccess, setHasVideoAccess] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  // 2026-02 · Cuando alguien llega por deep-link (?video=ID) a un video de
  // pago SIN sesión activa, mostramos un mensaje "inicia sesión / actualiza
  // membresía" en lugar de abrir el reproductor. Los videos marcados como
  // gratuitos (is_free=true) siguen abriéndose sin restricción.
  const [lockedDeepLink, setLockedDeepLink] = useState(null); // { title }
  
  // Estado para recetarios en video (nuevo sistema)
  const [recipeFolders, setRecipeFolders] = useState([]);
  const [recipeHasContent, setRecipeHasContent] = useState(false);
  const [selectedRecipeFolder, setSelectedRecipeFolder] = useState(null);
  const [recipeFolderVideos, setRecipeFolderVideos] = useState([]);

  // 2026-02 · Categorías personalizadas creadas desde el panel admin.
  // Se mergean con `CATEGORIES` (las fijas) para pintar carpetas extra.
  const [customCategories, setCustomCategories] = useState([]);

  // Usar el plan del user prop directamente
  const effectiveUserPlan = user?.subscription_plan?.toLowerCase()?.trim() || 'none';
  const isFreePlan = !effectiveUserPlan || effectiveUserPlan === 'none' || effectiveUserPlan === 'free';
  // Considerar como plan pago cualquier plan que no sea free/none/vacío
  const hasPaidPlan = effectiveUserPlan && effectiveUserPlan !== 'none' && effectiveUserPlan !== 'free' && effectiveUserPlan !== '';

  useEffect(() => {
    fetchVideos();
    fetchUserInfo();
    fetchRecipeContent();
    fetchCustomCategories();
  }, []);

  const fetchCustomCategories = async () => {
    try {
      const res = await axios.get(`${API}/video-categories`, { withCredentials: false });
      setCustomCategories(res.data?.items || []);
    } catch (error) {
      // Silencio: si el endpoint aún no está disponible el dashboard mantiene
      // sólo las categorías fijas.
      setCustomCategories([]);
    }
  };

  const fetchRecipeContent = async () => {
    try {
      // Verificar si hay contenido de recetarios
      const hasContentRes = await axios.get(`${API}/recipe-videos/has-content`);
      setRecipeHasContent(hasContentRes.data.has_content);
      
      // Cargar carpetas de recetarios
      const foldersRes = await axios.get(`${API}/recipe-videos/folders`);
      setRecipeFolders(foldersRes.data || []);
    } catch (error) {
      console.error('Error fetching recipe content:', error);
      setRecipeHasContent(false);
      setRecipeFolders([]);
    }
  };

  const fetchRecipeFolderVideos = async (folderId) => {
    try {
      const response = await axios.get(`${API}/recipe-videos/folders/${folderId}/videos`);
      setRecipeFolderVideos(response.data.videos || []);
    } catch (error) {
      console.error('Error fetching recipe folder videos:', error);
      setRecipeFolderVideos([]);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: false });
      const profileResponse = await axios.get(`${API}/user/profile`, { withCredentials: false });
      setUserPlan(profileResponse.data.subscription_plan || 'none');
      setHasVideoAccess(profileResponse.data.has_video_access || hasPaidPlan);
    } catch (error) {
      console.error('Error fetching user info:', error);
      setUserPlan('none');
      setHasVideoAccess(false);
    }
  };

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos`, { withCredentials: false });
      const videosData = response.data;
      
      // Ordenar videos alfabéticamente por defecto
      const sortedVideos = [...videosData].sort((a, b) => {
        // Primero por display_order si existe, luego alfabéticamente
        if (a.display_order !== undefined && b.display_order !== undefined) {
          return a.display_order - b.display_order;
        }
        return (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase());
      });
      
      setVideos(sortedVideos);
      
      // Agrupar videos por categoría (ya ordenados)
      const grouped = sortedVideos.reduce((acc, video) => {
        const category = video.category || 'sin_categoria';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(video);
        return acc;
      }, {});
      
      setGroupedVideos(grouped);
    } catch (error) {
      console.error('Error fetching videos:', error);
      setGroupedVideos({});
    } finally {
      setLoading(false);
    }
  };

  // Obtener videos accesibles para el usuario en una categoría
  const getAccessibleVideos = (categoryKey) => {
    const categoryVideos = groupedVideos[categoryKey] || [];
    
    // Si tiene plan de pago, puede ver todos los videos
    if (hasPaidPlan) {
      return categoryVideos;
    }
    
    // Si es usuario FREE, solo puede ver videos marcados como is_free
    return categoryVideos.filter(video => video.is_free === true);
  };

  // Contar videos gratuitos en una categoría
  const getFreeVideoCount = (categoryKey) => {
    const categoryVideos = groupedVideos[categoryKey] || [];
    return categoryVideos.filter(video => video.is_free === true).length;
  };

  const getVideoPrice = () => {
    if (userPlan === 'basic') {
      return { original: 50, discounted: 30, discount: '40%' };
    }
    return { original: 50, discounted: 50, discount: '0%' };
  };

  const handlePlayVideo = (video) => {
    if (hasVideoAccess) {
      setSelectedVideo(video);
    }
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
  };

  // 2026-02 · Sincroniza `selectedVideo` con el query param `?video=ID`.
  // Un ref evita que el montaje inicial borre el param (deep-link) antes
  // de que la biblioteca cargue.
  const prevSelectedIdRef = useRef(null);
  useEffect(() => {
    const current = searchParams.get('video');
    const prev = prevSelectedIdRef.current;
    if (selectedVideo && selectedVideo.id) {
      if (current !== selectedVideo.id) {
        const next = new URLSearchParams(searchParams);
        next.set('video', selectedVideo.id);
        setSearchParams(next, { replace: false });
      }
    } else if (prev) {
      // Sólo limpiamos si venimos de un video abierto (usuario cerró el modal).
      // Así el deep-link inicial (?video=ID) sobrevive hasta que la lista carga.
      if (current) {
        const next = new URLSearchParams(searchParams);
        next.delete('video');
        setSearchParams(next, { replace: false });
      }
    }
    prevSelectedIdRef.current = selectedVideo?.id || null;
  }, [selectedVideo]); // eslint-disable-line

  useEffect(() => {
    const wantedId = searchParams.get('video');
    if (!wantedId) return;
    if (selectedVideo && selectedVideo.id === wantedId) return;
    // Buscar el video en la biblioteca principal
    const fromLibrary = videos.find((v) => v && v.id === wantedId);
    if (fromLibrary) {
      // 2026-02 · Gate: video de pago + sin sesión → mensaje de bloqueo.
      const isFree = fromLibrary.is_free === true;
      if (!user && !isFree) {
        setLockedDeepLink({ title: fromLibrary.title || 'este video' });
        return;
      }
      setSelectedVideo(fromLibrary);
      return;
    }
    // Buscar en videos de carpeta de recetario (si están cargados)
    const fromRecipe = recipeFolderVideos.find((v) => v && v.id === wantedId);
    if (fromRecipe) {
      const isFreeR = fromRecipe.is_free === true;
      if (!user && !isFreeR) {
        setLockedDeepLink({ title: fromRecipe.title || 'este video' });
        return;
      }
      setSelectedVideo(fromRecipe);
    }
  }, [videos, recipeFolderVideos, searchParams, user]); // eslint-disable-line

  // Verificar si el usuario tiene acceso a una categoría por género Y por plan
  const canAccessCategory = (category, categoryKey) => {
    // Verificar acceso por género - SOLO si hay usuario logueado con género definido
    // Si no hay usuario o no tiene género configurado, NO bloquear por género
    const shouldCheckGender = user && hasGenderSet;
    const genderAccess = category.gender === 'all' || 
                         !shouldCheckGender ||  // Sin usuario/género = sin bloqueo por género
                         (category.gender === 'male' && isMale) || 
                         (category.gender === 'female' && isFemale);
    
    if (!genderAccess) return false;
    
    // Si tiene plan de pago, tiene acceso completo
    if (hasPaidPlan) return true;
    
    // Si es FREE, solo tiene acceso si hay videos gratuitos en esa categoría
    const freeVideosInCategory = getFreeVideoCount(categoryKey);
    return freeVideosInCategory > 0;
  };

  // Verificar si la categoría está bloqueada por ser usuario FREE sin contenido gratuito
  const isLockedForFreePlan = (categoryKey) => {
    if (hasPaidPlan) return false;
    const freeVideosInCategory = getFreeVideoCount(categoryKey);
    return freeVideosInCategory === 0;
  };

  // Obtener color de la categoría
  const getCategoryColors = (category) => {
    const colors = {
      blue: { bg: 'bg-blue-50', bgHover: 'group-hover:bg-blue-100', icon: 'text-blue-500', border: 'hover:border-blue-200', accent: 'text-blue-600' },
      pink: { bg: 'bg-pink-50', bgHover: 'group-hover:bg-pink-100', icon: 'text-pink-500', border: 'hover:border-pink-200', accent: 'text-pink-600' },
      purple: { bg: 'bg-purple-50', bgHover: 'group-hover:bg-purple-100', icon: 'text-purple-500', border: 'hover:border-purple-200', accent: 'text-purple-600' },
      yellow: { bg: 'bg-yellow-50', bgHover: 'group-hover:bg-yellow-100', icon: 'text-yellow-600', border: 'hover:border-yellow-200', accent: 'text-yellow-600' },
      red: { bg: 'bg-red-50', bgHover: 'group-hover:bg-red-100', icon: 'text-red-500', border: 'hover:border-red-200', accent: 'text-red-600' },
      green: { bg: 'bg-green-50', bgHover: 'group-hover:bg-green-100', icon: 'text-green-500', border: 'hover:border-green-200', accent: 'text-green-600' },
      orange: { bg: 'bg-orange-50', bgHover: 'group-hover:bg-orange-100', icon: 'text-orange-500', border: 'hover:border-orange-200', accent: 'text-orange-600' },
    };
    return colors[category.color] || colors.pink;
  };

  // 2026-02 · Modal del reproductor. Se define como variable JSX para poder
  // insertarlo en cualquier vista (grid de carpetas, detalle, recetarios) y
  // que el deep-link `?video=ID` funcione desde el URL directo.
  const closeLockedDeepLink = () => {
    setLockedDeepLink(null);
    // Quitar el `?video=` del URL para que un refresh no vuelva a disparar
    // el mensaje de bloqueo.
    const next = new URLSearchParams(searchParams);
    next.delete('video');
    setSearchParams(next, { replace: false });
  };

  const lockedDeepLinkModal = (
    <AnimatePresence>
      {lockedDeepLink && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90"
          onClick={closeLockedDeepLink}
          data-testid="video-locked-deeplink-modal"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative w-full max-w-md rounded-2xl p-8 shadow-2xl border"
            style={{
              backgroundColor: '#111',
              borderColor: 'rgba(255,255,255,0.08)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeLockedDeepLink}
              className="absolute top-3 right-3 p-2 rounded-full transition-colors"
              style={{ background: 'rgba(255,255,255,0.08)' }}
              aria-label="Cerrar"
            >
              <X className="text-white h-4 w-4" />
            </button>
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mb-5 mx-auto"
              style={{ background: 'rgba(var(--brand-primary-glow), 0.15)' }}
            >
              <Lock className="w-6 h-6" style={{ color: 'var(--brand-primary)' }} />
            </div>
            <h3 className="text-xl font-semibold text-white text-center mb-2">
              Contenido para miembros
            </h3>
            <p className="text-gray-400 text-sm text-center mb-6">
              El video "<span className="text-white">{lockedDeepLink.title}</span>" es contenido de pago.
              Inicia sesión o actualiza tu membresía para verlo.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => { closeLockedDeepLink(); navigate('/'); }}
                className="w-full"
                style={{ backgroundColor: 'var(--brand-primary)', color: 'white' }}
                data-testid="video-locked-login-btn"
              >
                Iniciar sesión
              </Button>
              <Button
                variant="outline"
                onClick={() => { closeLockedDeepLink(); navigate('/#planes'); }}
                className="w-full text-white border-white/20 hover:bg-white/10"
                data-testid="video-locked-plans-btn"
              >
                Ver membresías
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // 2026-02 · Modal del reproductor. Se define como variable JSX para poder
  // insertarlo en cualquier vista (grid de carpetas, detalle, recetarios) y
  // que el deep-link `?video=ID` funcione desde el URL directo.
  const videoPlayerModal = (
    <AnimatePresence>
      {selectedVideo && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95"
          onClick={closeVideoModal}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative w-full max-w-5xl rounded-2xl overflow-hidden shadow-2xl"
            style={{ backgroundColor: '#0a0a0a' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent">
              <h3 className="text-white font-semibold text-lg truncate pr-4">{selectedVideo.title}</h3>
              <button
                onClick={closeVideoModal}
                className="p-2 rounded-full transition-colors"
                style={{ background: 'rgba(255, 255, 255, 0.2)' }}
                data-testid="video-modal-close-btn"
              >
                <X className="text-white h-5 w-5" />
              </button>
            </div>
            <div className="relative pt-[56.25%]">
              {(() => {
                const embedUrl = getEmbedUrl(selectedVideo.video_url, selectedVideo.video_source);
                const useIframe = needsIframe(selectedVideo.video_url, selectedVideo.video_source);
                if (useIframe) {
                  return (
                    <iframe
                      src={embedUrl}
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; web-share"
                      allowFullScreen
                      title={selectedVideo.title}
                    />
                  );
                }
                return (
                  <video
                    src={embedUrl}
                    className="absolute inset-0 w-full h-full"
                    controls
                    autoPlay
                    playsInline
                    disablePictureInPicture
                    controlsList="nodownload"
                  >
                    Tu navegador no soporta el elemento de video.
                  </video>
                );
              })()}
            </div>
            <div className="p-4 bg-gray-900 max-h-24 overflow-y-auto border-t border-gray-800">
              <p className="text-gray-400 text-sm">{selectedVideo.description}</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="text-center">
          <div 
            className="w-16 h-16 rounded-full animate-pulse mx-auto mb-4"
            style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
          ></div>
          <p className="text-gray-400">{getText('Cargando videos...', 'Cargando videos...')}</p>
        </div>
      </div>
    );
  }

  // Vista de carpetas (categorías)
  if (!selectedCategory) {
    return (
      <div className="min-h-screen py-12 px-6 relative" style={{ backgroundColor: '#0a0a0a' }}>
        {/* Mesh gradient background */}
        <div 
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at 20% 80%, rgba(var(--brand-primary-glow), 0.12), transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 240, 255, 0.08), transparent 40%)'
          }}
        />
        
        <div className="relative z-10 max-w-6xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')} 
            className="mb-8 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver al Dashboard
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div 
              className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6"
              style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
            >
              <Video className="h-10 w-10 text-white" />
            </div>
            <h1 
              className="text-4xl md:text-5xl font-bold text-white mb-4 uppercase tracking-tight"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Biblioteca de Videos
            </h1>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              {getText(
                '¡Hola, Guerrero! Explora nuestro contenido exclusivo',
                '¡Hola, Guerrera! Explora nuestro contenido exclusivo'
              )}
            </p>
          </motion.div>

          {/* Grid de carpetas - Con verificación de género */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(() => {
              // 2026-02 · Mezclamos las categorías fijas + las personalizadas
              // que devuelve `/api/video-categories`. Cada personalizada usa
              // defaults visuales (icono Folder, color purple, gender 'all').
              const mergedEntries = [
                ...Object.entries(CATEGORIES),
                ...customCategories
                  .filter((c) => c?.slug && !(c.slug in CATEGORIES))
                  .map((c) => [
                    c.slug,
                    {
                      name: c.label,
                      description: 'Categoría personalizada',
                      Icon: Folder,
                      gender: 'all',
                      color: 'purple',
                      emoji: c.emoji || '',
                      isCustom: true,
                    },
                  ]),
              ];
              return mergedEntries.map(([key, category], idx) => {
              const totalVideoCount = groupedVideos[key]?.length || 0;
              const IconComponent = category.Icon;
              const colors = getCategoryColors(category);
              
              // Verificar acceso por género - SOLO si hay usuario logueado con género definido
              // Si no hay usuario o no tiene género, mostrar todo sin bloqueo
              const shouldCheckGender = user && hasGenderSet;
              const genderAccess = category.gender === 'all' || 
                                   !shouldCheckGender ||  // Sin usuario/género = sin bloqueo por género
                                   (category.gender === 'male' && isMale) || 
                                   (category.gender === 'female' && isFemale);
              const isGenderLocked = !genderAccess;
              
              // Para recetas saludables, usar contador de carpetas en lugar de videos
              const displayCount = key === 'recetas_saludables' 
                ? (recipeHasContent ? recipeFolders.length : 0)
                : totalVideoCount;
              const countLabel = key === 'recetas_saludables' 
                ? (recipeHasContent ? `${displayCount} categoría${displayCount === 1 ? '' : 's'}` : 'Próximamente')
                : `${displayCount} video${displayCount === 1 ? '' : 's'}`;
              
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <button
                    onClick={() => {
                      if (isGenderLocked) {
                        // No hacer nada si está bloqueado por género
                        return;
                      }
                      setSelectedCategory(key);
                    }}
                    disabled={isGenderLocked}
                    className={`w-full text-left p-6 rounded-2xl transition-all duration-300 group ${isGenderLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-[1.02]'}`}
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center transition-colors"
                        style={{ background: category.color === 'blue' ? 'rgba(0, 240, 255, 0.2)' : category.color === 'pink' ? 'rgba(var(--brand-primary-glow), 0.2)' : category.color === 'yellow' ? 'rgba(255, 200, 0, 0.2)' : category.color === 'red' ? 'rgba(255, 100, 100, 0.2)' : category.color === 'green' ? 'rgba(0, 200, 100, 0.2)' : 'rgba(255, 150, 0, 0.2)' }}
                      >
                        <IconComponent className="h-6 w-6" style={{ color: category.color === 'blue' ? '#00F0FF' : category.color === 'pink' ? 'var(--brand-primary)' : category.color === 'yellow' ? '#FFD700' : category.color === 'red' ? '#FF6B6B' : category.color === 'green' ? '#00E676' : '#FF9800' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white mb-1 truncate">
                          {category.name}
                        </h3>
                        <p className="text-sm text-gray-400 mb-2 line-clamp-1">
                          {category.description}
                        </p>
                        <span className="text-xs font-medium" style={{ color: key === 'recetas_saludables' && !recipeHasContent ? '#00E676' : 'var(--brand-primary)' }}>
                          {countLabel}
                        </span>
                      </div>
                      <div className="text-gray-400 group-hover:text-white transition-colors">
                        {isGenderLocked ? (
                          <Lock className="h-5 w-5 text-red-400" />
                        ) : (
                          <Play className="h-5 w-5" />
                        )}
                      </div>
                    </div>
                    {/* Mensaje de bloqueo por género */}
                    {isGenderLocked && (
                      <div className="mt-3 text-xs text-red-400 flex items-center gap-1">
                        <Lock className="h-3 w-3" />
                        {category.gender === 'male' ? 'Contenido exclusivo para hombres' : 'Contenido exclusivo para mujeres'}
                      </div>
                    )}
                  </button>
                </motion.div>
              );
            });
            })()}
          </div>

          {videos.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center mt-16 p-12 rounded-2xl"
              style={{ 
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <Folder className="h-16 w-16 mx-auto mb-4" style={{ color: 'var(--brand-primary)' }} />
              <h3 className="text-xl font-semibold text-white mb-2">No hay videos aún</h3>
              <p className="text-gray-500">
                Los videos aparecerán aquí cuando sean agregados.
              </p>
            </motion.div>
          )}
        </div>
        {videoPlayerModal}
        {lockedDeepLinkModal}
      </div>
    );
  }

  // Vista de videos dentro de una categoría - TODOS los videos con opción de desbloqueo
  const allCategoryVideos = groupedVideos[selectedCategory] || [];
  // 2026-02 · La categoría seleccionada puede ser fija o personalizada.
  const customCat = customCategories.find((c) => c.slug === selectedCategory);
  const categoryInfo = CATEGORIES[selectedCategory] || (customCat ? {
    name: customCat.label,
    description: 'Categoría personalizada',
    Icon: Folder,
    gender: 'all',
    color: 'purple',
  } : null);
  const CategoryIcon = categoryInfo?.Icon || Video;

  // CASO ESPECIAL: Recetas Saludables - usar el nuevo sistema de recetarios en video
  if (selectedCategory === 'recetas_saludables') {
    // Si no hay contenido, mostrar "Próximamente"
    if (!recipeHasContent) {
      return (
        <div className="min-h-screen py-12 px-6 relative" style={{ backgroundColor: '#0a0a0a' }}>
          <div 
            className="fixed inset-0 z-0 pointer-events-none"
            style={{
              background: 'radial-gradient(circle at 20% 80%, rgba(var(--brand-primary-glow), 0.12), transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 240, 255, 0.08), transparent 40%)'
            }}
          />
          <div className="relative z-10 max-w-6xl mx-auto">
            <Button 
              variant="ghost" 
              onClick={() => setSelectedCategory(null)} 
              className="mb-8 text-gray-400 hover:text-white"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Volver a Categorías
            </Button>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <div 
                className="w-32 h-32 mx-auto mb-8 rounded-full flex items-center justify-center"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(0, 230, 118, 0.2), rgba(0, 200, 100, 0.2))',
                  border: '2px solid rgba(0, 230, 118, 0.3)'
                }}
              >
                <ChefHat className="h-16 w-16" style={{ color: '#00E676' }} />
              </div>

              <h1 
                className="text-4xl md:text-5xl font-bold text-white mb-4"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Recetas Saludables
              </h1>

              <div 
                className="inline-block rounded-full px-6 py-2 mb-6"
                style={{ 
                  background: 'linear-gradient(135deg, #00E676, #00C853)',
                }}
              >
                <span className="text-white font-bold text-lg">🚧 PRÓXIMAMENTE</span>
              </div>

              <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
                Estamos preparando una colección increíble de recetas en video para ti. 
                <br />
                <span style={{ color: '#00E676' }}>¡Muy pronto podrás aprender a cocinar delicioso y saludable!</span>
              </p>

              <div 
                className="max-w-md mx-auto rounded-2xl p-6"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <h3 className="text-white font-semibold mb-4">Lo que vendrá:</h3>
                <ul className="text-gray-400 text-left space-y-2">
                  <li className="flex items-center gap-2">
                    <Play className="h-4 w-4" style={{ color: 'var(--brand-primary)' }} /> Videos paso a paso
                  </li>
                  <li className="flex items-center gap-2">
                    <Folder className="h-4 w-4" style={{ color: '#00F0FF' }} /> Organizados por categorías
                  </li>
                  <li className="flex items-center gap-2">
                    <ChefHat className="h-4 w-4" style={{ color: '#00E676' }} /> Recetas exclusivas
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
          {videoPlayerModal}
        </div>
      );
    }

    // Si hay contenido, mostrar las carpetas de recetarios
    // Si hay una carpeta seleccionada, mostrar sus videos
    if (selectedRecipeFolder) {
      return (
        <div className="min-h-screen py-12 px-6 relative" style={{ backgroundColor: '#0a0a0a' }}>
          <div 
            className="fixed inset-0 z-0 pointer-events-none"
            style={{
              background: 'radial-gradient(circle at 20% 80%, rgba(var(--brand-primary-glow), 0.12), transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 240, 255, 0.08), transparent 40%)'
            }}
          />
          <div className="relative z-10 max-w-6xl mx-auto">
            <Button 
              variant="ghost" 
              onClick={() => {
                setSelectedRecipeFolder(null);
                setRecipeFolderVideos([]);
              }} 
              className="mb-8 text-gray-400 hover:text-white"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Volver a Recetas
            </Button>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-10"
            >
              <h1 
                className="text-3xl font-bold text-white uppercase mb-2"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {selectedRecipeFolder.name}
              </h1>
              {selectedRecipeFolder.description && (
                <p className="text-gray-400">{selectedRecipeFolder.description}</p>
              )}
            </motion.div>

            {/* Grid de videos de la carpeta */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recipeFolderVideos.map((video, idx) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  onClick={() => setSelectedVideo(video)}
                  className="group cursor-pointer rounded-xl overflow-hidden"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img 
                      src={video.thumbnail || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop'} 
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                        <Play className="h-6 w-6 text-white ml-1" />
                      </div>
                    </div>
                    {video.duration && (
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-white group-hover:text-[#00E676] transition-colors">
                      {video.title}
                    </h3>
                    {video.description && (
                      <p className="text-gray-400 text-sm mt-1 line-clamp-2">{video.description}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {recipeFolderVideos.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No hay videos en esta carpeta aún.</p>
              </div>
            )}
          </div>
          {videoPlayerModal}
        </div>
      );
    }

    // Mostrar grid de carpetas de recetarios
    return (
      <div className="min-h-screen py-12 px-6 relative" style={{ backgroundColor: '#0a0a0a' }}>
        <div 
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at 20% 80%, rgba(var(--brand-primary-glow), 0.12), transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 240, 255, 0.08), transparent 40%)'
          }}
        />
        <div className="relative z-10 max-w-6xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedCategory(null)} 
            className="mb-8 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver a Categorías
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div 
              className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
              style={{ background: 'linear-gradient(135deg, #00E676, #00C853)' }}
            >
              <ChefHat className="h-8 w-8 text-white" />
            </div>
            <h1 
              className="text-3xl font-bold text-white uppercase mb-2"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Recetas Saludables
            </h1>
            <p className="text-gray-400">Preparaciones nutritivas y deliciosas</p>
          </motion.div>

          {/* Grid de carpetas de recetarios */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recipeFolders.map((folder, idx) => (
              <motion.div
                key={folder.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                onClick={() => {
                  setSelectedRecipeFolder(folder);
                  fetchRecipeFolderVideos(folder.id);
                }}
                className="group cursor-pointer rounded-xl overflow-hidden hover:scale-[1.02] transition-transform"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <div className="relative aspect-video overflow-hidden">
                  <img 
                    src={folder.thumbnail || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop'} 
                    alt={folder.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-3 left-3 flex items-center gap-2 text-white">
                    <Play className="h-4 w-4" style={{ color: '#00E676' }} />
                    <span className="text-sm font-medium">{folder.video_count || 0} videos</span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-white group-hover:text-[#00E676] transition-colors">
                    {folder.name}
                  </h3>
                  {folder.description && (
                    <p className="text-gray-400 text-sm mt-1 line-clamp-2">{folder.description}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        {videoPlayerModal}
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6 relative" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Mesh gradient background */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 20% 80%, rgba(var(--brand-primary-glow), 0.12), transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 240, 255, 0.08), transparent 40%)'
        }}
      />
      
      <div className="relative z-10 max-w-6xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => setSelectedCategory(null)} 
          className="mb-8 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Volver a Categorías
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="flex items-center gap-4 mb-2">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
            >
              <CategoryIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 
                className="text-3xl font-bold text-white uppercase"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {categoryInfo?.name}
              </h1>
              <p className="text-gray-400">
                {allCategoryVideos.length} videos disponibles
              </p>
            </div>
          </div>
        </motion.div>

        {/* Banner de desbloqueo si no tiene acceso */}
        {!hasPaidPlan && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 rounded-2xl"
            style={{ 
              background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.2), rgba(0, 240, 255, 0.2))',
              border: '2px solid rgba(var(--brand-primary-glow), 0.5)'
            }}
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2 justify-center md:justify-start">
                  <Lock className="h-5 w-5" style={{ color: 'var(--brand-primary)' }} />
                  Desbloquea toda la biblioteca
                </h3>
                <p className="text-gray-300">
                  Accede a todos los videos con una membresía o compra acceso individual
                </p>
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={() => window.location.href = '/#planes'}
                  className="rounded-full text-white font-bold px-6"
                  style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                >
                  Ver Membresías
                </Button>
                <Button 
                  onClick={() => navigate('/checkout?plan=videos&period=monthly')}
                  variant="outline"
                  className="rounded-full font-bold px-6 border-white/30 text-white hover:bg-white/10"
                >
                  Comprar Acceso
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {allCategoryVideos.length === 0 ? (
          <div 
            className="text-center p-16 rounded-2xl"
            style={{ 
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <Video className="h-16 w-16 mx-auto mb-4" style={{ color: 'var(--brand-primary)' }} />
            <h3 className="text-xl font-semibold text-white mb-2">Esta categoría está vacía</h3>
            <p className="text-gray-400">No hay videos en esta categoría aún.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allCategoryVideos.map((video, idx) => {
              // Determinar si el usuario puede ver este video
              const canWatchVideo = video.is_free || hasPaidPlan || hasVideoAccess;
              
              return (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <div 
                    className="overflow-hidden rounded-2xl transition-all duration-300 hover:scale-[1.02] relative"
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <div className="relative group">
                      <img
                        src={video.thumbnail_url || `https://via.placeholder.com/400x225/FF2E93/ffffff?text=${encodeURIComponent(video.title)}`}
                        alt={video.title}
                        className={`w-full h-48 object-cover ${!canWatchVideo ? 'filter brightness-50' : ''}`}
                        onError={(e) => {
                          e.target.src = `https://via.placeholder.com/400x225/FF2E93/ffffff?text=${encodeURIComponent(video.title)}`;
                        }}
                      />
                      
                      {/* Badge de GRATIS si el video es gratuito */}
                      {video.is_free && (
                        <Badge 
                          className="absolute top-2 left-2 text-white text-xs font-bold border-0"
                          style={{ background: '#CCFF00', color: '#000' }}
                        >
                          ✓ GRATIS
                        </Badge>
                      )}

                      {/* Overlay de BLOQUEO para videos premium */}
                      {!canWatchVideo && (
                        <div 
                          className="absolute inset-0 flex flex-col items-center justify-center bg-black/70"
                        >
                          <Lock className="h-10 w-10 text-white mb-2" />
                          <span className="text-white text-sm font-bold">PREMIUM</span>
                        </div>
                      )}
                      
                      {/* Overlay de play para videos accesibles */}
                      {canWatchVideo && (
                        <div 
                          className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          onClick={() => setSelectedVideo(video)}
                        >
                          <div 
                            className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg"
                            style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                          >
                            <Play className="h-7 w-7 text-white ml-1" fill="currentColor" />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-lg font-bold text-white line-clamp-1 mb-1">{video.title}</h3>
                      <p className="text-gray-400 text-sm line-clamp-2 mb-4">
                        {video.description || 'Video de entrenamiento'}
                      </p>
                      
                      {canWatchVideo ? (
                        <Button
                          className="w-full rounded-full text-white font-bold"
                          style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                          onClick={() => setSelectedVideo(video)}
                        >
                          <Play className="mr-2 h-4 w-4" /> {video.is_free ? 'Ver Gratis' : 'Reproducir'}
                        </Button>
                      ) : (
                        <div className="space-y-2">
                          <Button
                            className="w-full rounded-full text-white font-bold"
                            style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                            onClick={() => navigate('/plans')}
                          >
                            <Lock className="mr-2 h-4 w-4" /> Desbloquear
                          </Button>
                          <p className="text-xs text-gray-500 text-center">
                            Activa una membresía para acceder
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {videoPlayerModal}
      {lockedDeepLinkModal}
    </div>
  );
};

export default VideosOrganized;
